import { useCallback } from 'react'
import snakeCase from 'lodash/snakeCase'

import { TestIds } from '@app/config/constants/TestIds'

// =============================================================================
// TYPE DEFINITIONS
// =============================================================================

/**
 * Represents a nested object structure for test IDs
 */
type TestIdObject = {
  [key: string]: string | TestIdObject
}

// =============================================================================
// SIMPLIFIED TYPE SYSTEM FOR BETTER INTELLISENSE
// =============================================================================

/**
 * Helper type to get nested paths as string literals (simplified)
 */
type GetPaths<T, Prefix extends string = ''> =
  T extends Record<string, any>
    ? {
        [K in keyof T]: T[K] extends string
          ? Prefix extends ''
            ? Lowercase<string & K>
            : `${Prefix}.${Lowercase<string & K>}`
          : T[K] extends Record<string, any>
            ? Prefix extends ''
              ? Lowercase<string & K> | GetPaths<T[K], Lowercase<string & K>>
              : `${Prefix}.${Lowercase<string & K>}` | GetPaths<T[K], `${Prefix}.${Lowercase<string & K>}`>
            : never
      }[keyof T]
    : never

/**
 * All available test ID paths from TestIds
 */
type AllTestIdPaths = GetPaths<typeof TestIds>

/**
 * Helper type to get all keys from a nested object at any level with dot notation
 */
type GetAllKeys<T, Prefix extends string = ''> =
  T extends Record<string, any>
    ? {
        [K in keyof T]: K extends string
          ? T[K] extends string
            ? Prefix extends ''
              ? Lowercase<K>
              : `${Prefix}.${Lowercase<K>}`
            : T[K] extends Record<string, any>
              ? Prefix extends ''
                ? Lowercase<K> | GetAllKeys<T[K], Lowercase<K>>
                : `${Prefix}.${Lowercase<K>}` | GetAllKeys<T[K], `${Prefix}.${Lowercase<K>}`>
              : never
          : never
      }[keyof T]
    : never

/**
 * Get all available keys with dot notation when scoped to a specific section
 */
type ScopedKeys<TScope extends string> = TScope extends keyof typeof TestIds
  ? GetAllKeys<(typeof TestIds)[TScope]>
  : never /**
 * Top-level keys of TestIds object in lowercase
 */
export type TestIdGroupPathType = keyof typeof TestIds extends infer K
  ? K extends string
    ? `${Lowercase<K>}` extends infer C
      ? C extends string
        ? C
        : never
      : never
    : never
  : never

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Converts a path (string or array) to an array of uppercase snake_case strings
 *
 * @param path - The path to parse
 * @returns Array of uppercase snake_case strings
 *
 * @example
 * parsePath(['side_navigation']) // ['SIDE_NAVIGATION']
 * parsePath('side_navigation.toggle') // ['SIDE_NAVIGATION', 'TOGGLE']
 */
function parsePath(path: readonly string[] | string | undefined): string[] {
  const arrayPath = Array.isArray(path) ? [...path] : typeof path === 'string' ? path.split('.') : []
  return arrayPath.map((p) => snakeCase(p).toUpperCase())
}

/**
 * Converts a test ID path to an array of uppercase snake_case strings
 *
 * @param id - The test ID path to parse (can be string, array, or template literal)
 * @returns Array of uppercase snake_case strings
 *
 * @example
 * parseIdPath('toggle') // ['TOGGLE']
 * parseIdPath('page.game_tabs.classroom') // ['PAGE', 'GAME_TABS', 'CLASSROOM']
 * parseIdPath(`page.game_tabs.${id}`) // ['PAGE', 'GAME_TABS', 'CLASSROOM']
 */
function parseIdPath(id: string | string[]): string[] {
  // Handle string input (including template literals)
  if (typeof id === 'string') {
    return id.split('.').map((p) => snakeCase(p).toUpperCase())
  }

  // Handle array input
  const arrayPath = Array.isArray(id) ? id : []
  return arrayPath.map((p) => snakeCase(p).toUpperCase())
} /**
 * Traverses a nested object following a given path
 *
 * @param source - The object to traverse
 * @param path - Array of keys representing the path to follow
 * @param onStringInTheMiddle - Optional callback for handling string values in the middle of the path
 * @returns The value found at the end of the path
 * @throws Error in development mode if the path is not found
 */
function followPath(
  source: string | TestIdObject,
  path: string[],
  onStringInTheMiddle?: (path: string[]) => string
): string | TestIdObject {
  let result = source

  function throwNotFound(index: number): never {
    const message =
      `[TEST_ID] Path ${JSON.stringify(path)} not found. ` +
      `Found part: ${JSON.stringify(path.slice(0, index))} ` +
      `Missing: ${JSON.stringify(path.slice(index))} ` +
      `In object: ${JSON.stringify(source, null, 2)}`

    console.warn(message)
    return '<unknown>' as never
  }

  for (let index = 0; index < path.length; index++) {
    // If we hit a string value before reaching the end of the path
    if (typeof result === 'string') {
      if (onStringInTheMiddle) {
        // Append remaining path as kebab-case to the string
        result += `-${onStringInTheMiddle(path.slice(index))}`
        break
      }

      // In development, throw an error for invalid paths
      if (import.meta.env.DEV) {
        return throwNotFound(index)
      } else {
        return '<unknown>'
      }
    }

    const part = path[index]

    // Check if the key exists in the current object
    if (!(part in result)) {
      if (import.meta.env.DEV) {
        return throwNotFound(index)
      } else {
        return '<unknown>'
      }
    }

    result = result[part] as TestIdObject
  }

  return result
}

// =============================================================================
// MAIN HOOK
// =============================================================================

/**
 * Valid scope arrays that can be passed to useTestId - dynamically generated from TestIds keys
 *
 * This automatically includes any top-level keys from TestIds object (in lowercase).
 * To add new scopes, simply add new top-level keys to TestIds.ts
 *
 * @example
 * // If TestIds has: { SIDE_NAVIGATION: {...}, LEARNING_PLAN: {...}, NEW_SECTION: {...} }
 * // Then valid scopes are: ['side_navigation'], ['learning_plan'], ['new_section']
 */
type ValidScope = readonly [TestIdGroupPathType, ...string[]]

// =============================================================================
// MAIN HOOK
// =============================================================================

/**
 * A React hook that generates test IDs based on a scoped path structure
 *
 * @example
 * const testId = useTestId(['side_navigation'])
 * testId('languages.english') // Gets IntelliSense for dot-separated paths in SIDE_NAVIGATION
 *
 * @example
 * const testId = useTestId(['learning_plan'])
 * testId('activity_detail.add_to_workspace') // Gets IntelliSense for dot-separated paths in LEARNING_PLAN
 */
export function useTestId(): {
  (id: AllTestIdPaths, options?: string): string
  (id: string, options?: string): string
}
export function useTestId<TScope extends TestIdGroupPathType>(
  path: readonly [TScope]
): {
  (id: ScopedKeys<Uppercase<TScope>>, options?: string): string
  (id: string, options?: string): string
}
export function useTestId<const TScope extends ValidScope | undefined = undefined>(path?: TScope) {
  const implementation = useCallback(
    (id: string, options?: string): string => {
      // Parse the base path and the specific ID path
      const computedPath = parsePath(path as readonly string[] | undefined)
      const computedId = parseIdPath(id as string)

      // Navigate to the base scope in TestIds
      const pathTarget = followPath(TestIds, computedPath)

      // Ensure the base path leads to an object, not a string
      if (typeof pathTarget === 'string') {
        if (import.meta.env.DEV) {
          console.warn(
            `[TEST_ID] Hook parameter path ${JSON.stringify(path)} leads to a string value. ` +
              `Paths should point to objects containing test IDs.`
          )
        }
        return '<unknown>'
      }

      // Navigate within the scoped object to find the final test ID
      const idTarget = followPath(
        pathTarget,
        computedId,
        // If we hit a string in the middle, append remaining path as kebab-case
        (remainingPath) => remainingPath.map((p) => p.toLowerCase()).join('-')
      )

      // If we found a valid test ID string, return it
      if (typeof idTarget === 'string') {
        return idTarget + (options ? `-${options}` : '')
      }

      // If no valid test ID was found, create a dynamic one from the input and warn
      if (import.meta.env.DEV) {
        console.warn(
          `[TEST_ID] ID "${id}" not found in TestIds.` +
            (path ? ` Scope: ${JSON.stringify(path)}.` : '') +
            ` Creating dynamic test ID from input.`
        )
      }

      // Create a test ID from the original input
      const dynamicId =
        typeof id === 'string'
          ? id
              .split('.')
              .map((p) => snakeCase(p))
              .join('-')
          : Array.isArray(id)
            ? (id as string[]).map((p) => snakeCase(p)).join('-')
            : 'unknown'

      return dynamicId + (options ? `-${options}` : '')
    },
    [path]
  )

  return implementation as any
}
